import { Component, effect, OnInit, inject } from "@angular/core";
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router, RouterModule } from "@angular/router";
import { BasemapService } from "./modules/basemap/basemap.service";
import { OptionService } from "./modules/option/option.service";
import { DataModeService } from "./modules/menu/data-mode.service";
import { PermissionService } from "./modules/core/services/permission.service";
import { InitService } from "@services/init.service";
import { LoadingService } from "./modules/loading/loading.service";
import { HeaderNavigationService } from "@services/header-navigation.service";
import { DateAdapter } from "@angular/material/core";
import { getDateFnsLocale } from "./modules/core/helper/use-dateFns-locale";
import { MapViewService } from "@services/map-view.service";
import { Coordinate } from "./shared/contract/Address/Coordinates";
import { EPSGType } from "./shared/contract/Enum/EPSG";
import { FormsModule } from "@angular/forms";
import { MatIconModule } from "@angular/material/icon";
import { MatSnackBarModule } from "@angular/material/snack-bar";
import { TranslocoModule } from "@jsverse/transloco";
import { AsyncPipe } from "@angular/common";
import { HeaderComponent } from "./modules/header/header/header.component";
import { LoadingComponent } from "./modules/loading/loading/loading.component";
import { filter } from "rxjs";
import { PortfolioDetailsService } from "./modules/portfolio-details/portfolio-details.service";
import { MatDialog } from "@angular/material/dialog";
import { CacheComponent } from "./modules/cache/cache.component";
import { LandingPageDialogComponent } from "./modules/reception/landing-page-dialog/landing-page-dialog.component";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  imports: [FormsModule, MatIconModule, TranslocoModule, MatSnackBarModule, RouterModule, AsyncPipe, HeaderComponent, LoadingComponent]
})
export class AppComponent implements OnInit {
  readonly loadingService = inject(LoadingService);
  readonly optionService = inject(OptionService);
  readonly permissionService = inject(PermissionService);
  readonly initService = inject(InitService);
  readonly subheaderNavigationService = inject(HeaderNavigationService);
  readonly matDialog = inject(MatDialog);
  private readonly mapViewService = inject(MapViewService);
  private readonly dataModeService = inject(DataModeService);
  private readonly dateAdapter = inject<DateAdapter<any>>(DateAdapter);
  private readonly baseMapService = inject(BasemapService);
  private readonly router = inject(Router);
  private readonly portfolioDetailsService = inject(PortfolioDetailsService);

  public title = "Accumate";
  protected hasShop$ = this.permissionService.hasShopRight();

  constructor() {
    effect(() => {
      const locale = this.optionService.locale();
      this.setMatDateLocal(locale);
    });

    this.setLoadingOnNavigation();
  }

  private setLoadingOnNavigation() {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.loadingService.show();
      } else if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError) {
        this.loadingService.hide();
      }
    });
  }
  private setMatDateLocal(locale: string) {
    let _locale: string | null = null;
    switch (locale) {
      case "de-DE":
        _locale = "de";
        break;
      case "en-GB":
        _locale = "enGB";
        break;
      case "fr-BE":
        _locale = "fr";
        break;
      case "nl-BE":
        _locale = "nlBE";
        break;
      case "es-ES":
      case "es-CL":
        _locale = "es";
        break;
    }

    this.dateAdapter.setLocale(getDateFnsLocale(_locale));
  }

  ngOnInit() {
    this.loadingService.show();
    this.baseMapService.isSatelliteImage.set(false);
    this.dataModeService.refreshAllowedModes();
    this.loadingService.hide();

    this.permissionService.refreshQuota().subscribe(() => {
      if (this.permissionService.isBlockedByQuota()) {
        this.navigateToReceptionWithoutQuota();
      }
    });

    const lastCoord = this.initService.InitData?.LastCoordinate;
    if (lastCoord) {
      const myBaseCoordinate: Coordinate = new Coordinate([lastCoord.Longitude, lastCoord.Latitude], EPSGType.LonLat);
      this.mapViewService.setViewByCoordinates(myBaseCoordinate.Get(EPSGType.OSM), 13);
    }

    //prevent that portfolio objects do not disappear from the map
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      if (!event.urlAfterRedirects.startsWith("/portfolio")) {
        this.portfolioDetailsService.selectedPortfolioId.set(null);
      }
    });
  }

  openCacheDialog() {
    this.matDialog.open(CacheComponent);
  }

  goToHome() {
    this.dataModeService.resetSelectedDataMode();
    this.router.navigateByUrl("/").then();
  }

  navigateToReceptionWithoutQuota() {
    if (this.permissionService.isBlockedByQuota()) {
      this.matDialog.open(LandingPageDialogComponent);
    }
  }
}
